import Head from 'next/head';
import React from 'react';
import type {
  WithContext,
  Article,
  WebPage,
  Organization,
  Person,
  Product as SchemaOrgProduct,
  ItemList,
} from 'schema-dts';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'shared/library/use-router';
import { useCurrentRoute, useRoute } from 'shared/library/routes/route';
import { LanguageCode, languageCodes } from 'shared/library/i18n/languages';
import type { Product, ProductCategory, Listing } from 'shared/@generated/models';
import type { Paginator } from 'shared/@types';
import { buildPaginationHref } from 'shared/components/pagination/pagination-url';
import { serializeJsonLd } from 'shared/library/json-ld';
import {
  buildCategorySchema,
  buildListingSchema,
  buildProductSchema,
  type CategorySchemaInput,
  type ProductSchemaOptions,
} from './seo-structured-data';
import { staticImageUrl } from 'shared/library/static-images';

type JsonLdSchema =
  | WithContext<Article>
  | WithContext<WebPage>
  | WithContext<Organization>
  | WithContext<Person>
  | WithContext<SchemaOrgProduct>
  | WithContext<ItemList>;

const META_DESCRIPTION_SENTENCE_SEARCH_MIN = 80;
const META_DESCRIPTION_MAX_LENGTH = 170;
const ORGANIZATION_ID = 'https://getapet.com/#organization';
const ORGANIZATION_LOGO = staticImageUrl('open-graph');
const CONTENT_AUTHOR_ID = 'https://getapet.com/#author-andre-andersson';
const CONTENT_AUTHOR_IMAGE = staticImageUrl('andre', 'full', 768);
const META_DESCRIPTION_CONTINUATION_MARKER = '...';
const META_DESCRIPTION_TRAILING_STOP_WORDS = new Set([
  'and',
  'or',
  'to',
  'for',
  'with',
  'from',
  'och',
  'eller',
  'att',
  'og',
  'at',
]);

function stripQueryAndHash(path: string) {
  const [withoutQuery = ''] = path.split('?');
  const [withoutHash = ''] = withoutQuery.split('#');
  return withoutHash;
}

function trimTrailingSlash(path: string) {
  return path === '/' ? path : path.replace(/\/$/, '');
}

function hasLanguagePrefix(path: string) {
  return languageCodes.some((lang) => path === `/${lang}` || path.startsWith(`/${lang}/`));
}

function getCurrentCanonicalPath(asPath: string, languageCode: LanguageCode) {
  const strippedPath = stripQueryAndHash(asPath).trim() || '/';
  const rootedPath = strippedPath.startsWith('/') ? strippedPath : `/${strippedPath}`;
  const normalizedPath = trimTrailingSlash(rootedPath);

  if (normalizedPath === '/') {
    return `/${languageCode}`;
  }

  if (hasLanguagePrefix(normalizedPath)) {
    return normalizedPath;
  }

  return trimTrailingSlash(`/${languageCode}${normalizedPath}`);
}

function appendContinuationMarker(description: string) {
  const maxLength = META_DESCRIPTION_MAX_LENGTH - META_DESCRIPTION_CONTINUATION_MARKER.length;
  const trimmedDescription = description.slice(0, maxLength).trim();

  return `${trimmedDescription}${META_DESCRIPTION_CONTINUATION_MARKER}`;
}

function stripTrailingStopWords(description: string) {
  let result = description.replace(/[,:;]+$/, '').trim();

  while (result) {
    const trailingWord = result.match(/([^\s.,:;!?]+)$/)?.[1]?.toLowerCase();

    if (!trailingWord || !META_DESCRIPTION_TRAILING_STOP_WORDS.has(trailingWord)) {
      break;
    }

    result = result
      .slice(0, -trailingWord.length)
      .replace(/[,:;]+$/, '')
      .trim();
  }

  return result;
}

function findSentenceEnd(description: string) {
  const sentenceEndPattern = /[.!?](?=\s|$)/g;
  let lastSentenceEndIndex = -1;
  let match: RegExpExecArray | null = null;

  while ((match = sentenceEndPattern.exec(description))) {
    if (match.index + 1 >= META_DESCRIPTION_SENTENCE_SEARCH_MIN) {
      lastSentenceEndIndex = match.index;
    }
  }

  return lastSentenceEndIndex;
}

export const truncateMetaDescription = (description: string) => {
  const normalizedDescription = description.replace(/\s+/g, ' ').trim();

  if (normalizedDescription.length <= META_DESCRIPTION_MAX_LENGTH) {
    return normalizedDescription;
  }

  const maxLengthSlice = normalizedDescription.slice(0, META_DESCRIPTION_MAX_LENGTH);
  const sentenceStopIdx = findSentenceEnd(maxLengthSlice);

  if (sentenceStopIdx !== -1) {
    return normalizedDescription.slice(0, sentenceStopIdx + 1).trim();
  }

  const lastWordBoundaryIdx = maxLengthSlice.lastIndexOf(' ');

  if (lastWordBoundaryIdx > 0) {
    const wordBoundaryDescription = stripTrailingStopWords(
      maxLengthSlice.slice(0, lastWordBoundaryIdx),
    );

    return appendContinuationMarker(wordBoundaryDescription);
  }

  return appendContinuationMarker(maxLengthSlice);
};

interface ArticleSchemaOptions {
  about?: string;
  citations?: string[];
}

export class SeoMeta {
  private readonly languageCode: LanguageCode;
  private readonly routeFn: (lang: LanguageCode) => string;
  private title: string;
  private description: string;
  private image: string;
  private robots: string = 'max-image-preview:large';
  private jsonLdSchemas: JsonLdSchema[] = [];
  private ogType: string = 'website';
  private readonly includeLanguageAlternates: boolean;
  private paginationPage = 1;

  constructor(
    languageCode: LanguageCode,
    routeFn: (lang: LanguageCode) => string,
    defaultTitle: string,
    defaultDescription: string,
    defaultImage: string,
    includeLanguageAlternates = true,
  ) {
    this.languageCode = languageCode;
    this.routeFn = routeFn;
    this.title = defaultTitle;
    this.description = truncateMetaDescription(defaultDescription);
    this.image = defaultImage;
    this.includeLanguageAlternates = includeLanguageAlternates;
  }

  setTitle(title?: string, includeAppName = true) {
    if (!title) return this;
    this.title = includeAppName ? `${title} | Get a Pet` : title;
    return this;
  }

  setNoIndex() {
    this.robots = 'noindex,follow,max-image-preview:large';
    return this;
  }

  setPagination(paginator: Pick<Paginator<unknown>, 'current_page' | 'last_page' | 'data'>) {
    this.paginationPage =
      Number.isSafeInteger(paginator.current_page) && paginator.current_page > 1
        ? paginator.current_page
        : 1;

    if (
      this.paginationPage > 1 &&
      (this.paginationPage > paginator.last_page || paginator.data.length === 0)
    ) {
      this.setNoIndex();
    }

    return this;
  }

  setDescription(description?: string) {
    if (!description) return this;
    this.description = truncateMetaDescription(description);
    return this;
  }

  setImage(image?: string) {
    if (!image) return this;
    this.image = image;
    return this;
  }

  private pushJsonLdSchema(schema: JsonLdSchema) {
    this.jsonLdSchemas.push(schema);
    return this;
  }

  forPage(title: string, description: string, image?: string, includeAppName = true) {
    return this.setTitle(title, includeAppName)
      .setDescription(description)
      .setImage(image)
      .pushJsonLdSchema({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${this.currentUrl()}#webpage`,
        name: title,
        description,
        url: this.currentUrl(),
        image: this.image,
        alternateName: 'getapet.com',
        author: {
          '@type': 'Organization',
          '@id': ORGANIZATION_ID,
          name: 'Get a Pet',
          alternateName: 'Skaffa Hund',
          telephone: '0706551301',
          image: ORGANIZATION_LOGO,
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Skeppargatan 92',
            addressLocality: 'Stockholm',
            postalCode: '11530',
            addressCountry: 'SE',
          },
        },
      })
      .pushJsonLdSchema({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': ORGANIZATION_ID,
        name: 'Get a Pet',
        legalName: 'Dogs Unleashed AB',
        alternateName: 'Skaffa Hund',
        url: 'https://getapet.com',
        logo: ORGANIZATION_LOGO,
        email: 'support@getapet.com',
        foundingDate: '2019',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Skeppargatan 92',
          addressLocality: 'Stockholm',
          postalCode: '11530',
          addressCountry: 'SE',
        },
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+46706551301',
          contactType: 'Customer Support',
        },
        sameAs: [
          'https://instagram.com/getapet_com',
          'https://facebook.com/getapetcom',
          'https://linkedin.com/company/getapetcom',
          'https://tiktok.com/@getapet_com',
          'https://trustpilot.com/review/getapet.com',
        ],
      });
  }

  forArticle(
    title: string,
    description: string,
    image?: string,
    publishedAt?: Date,
    updatedAt?: Date,
    options: ArticleSchemaOptions = {},
  ) {
    this.ogType = 'article';

    const url = this.currentUrl();

    this.forPage(title, description, image, false).pushJsonLdSchema({
      '@context': 'https://schema.org',
      '@type': 'Person',
      '@id': CONTENT_AUTHOR_ID,
      name: 'André Andersson',
      url: 'https://linkedin.com/in/anderssonandre',
      jobTitle: 'Editor and pet expert',
      image: CONTENT_AUTHOR_IMAGE,
      email: 'andre@skaffahund.se',
      sameAs: ['https://linkedin.com/in/anderssonandre', 'https://facebook.com/andreandersson01'],
      worksFor: {
        '@type': 'Organization',
        '@id': ORGANIZATION_ID,
        name: 'Get a Pet',
      },
    });

    return this.pushJsonLdSchema({
      '@context': 'https://schema.org',
      '@type': 'Article',
      '@id': `${url}#article`,
      headline: title,
      image: [this.image],
      url,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
      },
      ...(publishedAt ? { datePublished: publishedAt.toISOString() } : {}),
      ...(updatedAt ? { dateModified: updatedAt.toISOString() } : {}),
      ...(options.about
        ? {
            about: {
              '@type': 'Thing',
              name: options.about,
            },
          }
        : {}),
      ...(options.citations?.length ? { citation: options.citations } : {}),
      author: {
        '@type': 'Person',
        '@id': CONTENT_AUTHOR_ID,
        name: 'André Andersson',
        url: 'https://linkedin.com/in/anderssonandre',
      },
      publisher: {
        '@type': 'Organization',
        '@id': ORGANIZATION_ID,
        name: 'Get a Pet',
      },
    });
  }

  forProduct(product: Product, options?: ProductSchemaOptions) {
    return this.pushJsonLdSchema(
      buildProductSchema({
        product,
        url: options?.url ?? this.currentUrl(),
        fallbackImage: this.image,
        shopSettings: options?.shopSettings,
      }),
    );
  }

  forListing(listing: Listing, options?: ProductSchemaOptions) {
    return this.pushJsonLdSchema(
      buildListingSchema({
        listing,
        url: options?.url ?? this.currentUrl(),
        fallbackImage: this.image,
      }),
    );
  }

  forCategory(category: ProductCategory, input?: CategorySchemaInput) {
    return this.pushJsonLdSchema(
      buildCategorySchema({
        category,
        url: input?.url ?? this.currentUrl(),
        numberOfItems: input?.numberOfItems,
        items: input?.items,
      }),
    );
  }

  private currentUrl() {
    return this.urlInLanguage(this.languageCode) || 'https://getapet.com';
  }

  private urlInLanguage(lang: LanguageCode) {
    const path = this.routeFn(lang);

    if (!path) {
      return '';
    }

    return `https://getapet.com${buildPaginationHref(path, this.paginationPage)}`;
  }

  generateTags() {
    const xDefaultHref = this.urlInLanguage('en');
    const socialImage = this.image;

    return (
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover, interactive-widget=resizes-content"
        />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <link rel="icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
        <link rel="manifest" href="/site.webmanifest" />
        <link rel="icon" href="/favicon-32x32.png" sizes="32x32" />
        <link rel="icon" href="/favicon-16x16.png" sizes="16x16" />
        <title>{this.title}</title>
        <meta name="description" content={this.description} />
        <meta name="robots" content={this.robots} />
        <meta itemProp="name" content={this.title} />
        <meta itemProp="description" content={this.description} />
        <meta itemProp="image" content={this.image} />
        <meta property="og:site_name" content="Get a Pet" />
        <meta property="og:logo" content={ORGANIZATION_LOGO} />
        <meta property="og:url" content={this.currentUrl()} />
        <meta property="og:type" content={this.ogType} />
        <meta property="og:title" content={this.title} />
        <meta property="og:description" content={this.description} />
        <meta property="og:image" content={socialImage} />
        <meta property="og:locale" content={this.languageCode} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={this.title} />
        <meta name="twitter:description" content={this.description} />
        <meta name="twitter:image" content={socialImage} />
        <meta name="twitter:site" content="@getapet_com" />
        <meta name="twitter:creator" content="@getapet_com" />
        <meta property="og:see_also" content="https://instagram.com/getapet_com" />
        <meta property="og:see_also" content="https://facebook.com/getapetcom" />
        <meta property="og:see_also" content="https://www.linkedin.com/company/getapetcom" />
        <meta property="og:see_also" content="https://tiktok.com/@getapet_com" />
        {this.jsonLdSchemas.map((schema, idx) => (
          <script
            key={`jsonld-${idx}`}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: serializeJsonLd(schema) }}
          />
        ))}
        <link rel="canonical" href={this.currentUrl()} />
        {this.includeLanguageAlternates &&
          languageCodes.map((lang) => {
            const href = this.urlInLanguage(lang);

            if (!href) {
              return null;
            }

            return <link key={lang} rel="alternate" href={href} hrefLang={lang} />;
          })}
        {this.includeLanguageAlternates && xDefaultHref && (
          <link rel="alternate" href={xDefaultHref} hrefLang="x-default" />
        )}
      </Head>
    );
  }
}

const useSeo = () => {
  const { t } = useTranslation();
  const { asPath, languageCode } = useRouter();
  const currentRoute = useCurrentRoute();
  const route = useRoute();

  const canonicalPath = getCurrentCanonicalPath(asPath, languageCode);

  // The page mutates this builder, so each render needs fresh metadata.
  return new SeoMeta(
    languageCode,
    (lang: LanguageCode) => {
      if (!currentRoute.matched) {
        return lang === languageCode ? canonicalPath : '';
      }

      return route(currentRoute.name, currentRoute.params, { lang });
    },
    t('web:seo.title'),
    t('web:seo.description'),
    ORGANIZATION_LOGO,
    currentRoute.matched,
  );
};

export { useSeo };
